import type { ReactNode } from 'react';
import {
  Box,
  ChipGroup,
  FieldSet,
  FilterChip,
  FormHelp,
  Legend,
  Paragraph,
  TextButton,
} from '@zillow/constellation-10';

type ChipOption = {
  value: string;
  label: string;
};

type ChipSelectFieldProps = {
  label: string;
  name: string;
  options: ReadonlyArray<ChipOption>;
  value: string | null;
  onChange: (value: string | null) => void;
  /** Invoked when the "Clear" button is clicked. */
  onClear?: () => void;
  /** Help text rendered under the legend. */
  helpText?: string;
  /** Content rendered below the chips (e.g. a "Learn more" note). */
  bottomHelpText?: ReactNode;
  /** Inline content rendered next to the label (e.g. an info Popover). */
  labelInfo?: ReactNode;
};

const isValueValid = (value: string | null, options: ReadonlyArray<ChipOption>) =>
  Boolean(value && options.some((option) => option.value === value));

export const ChipSelectField = ({
  label,
  name,
  options,
  value,
  onChange,
  onClear,
  helpText,
  bottomHelpText,
  labelInfo,
}: ChipSelectFieldProps) => {
  const handleSelectionChange = (selected: string | Array<string> | undefined) => {
    onChange(typeof selected === 'string' ? selected : null);
  };

  const legendContent = (
    <Legend css={{ width: '100%' }}>
      <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}>
          <Paragraph textStyle="body-sm-bold" data-testid={`${name}-label`}>
            {label}
          </Paragraph>
          {labelInfo}
        </Box>
        {isValueValid(value, options) && (
          <TextButton
            data-testid={`${name}-clear-button`}
            onClick={() => {
              onChange(null);
              onClear?.();
            }}
          >
            Clear
          </TextButton>
        )}
      </Box>
    </Legend>
  );

  return (
    <FieldSet
      legend={legendContent}
      description={
        <>
          {helpText && <FormHelp>{helpText}</FormHelp>}
          {bottomHelpText}
        </>
      }
    >
      <ChipGroup
        aria-label={label}
        role="radiogroup"
        selected={value ?? undefined}
        onSelectionChange={handleSelectionChange}
        verticalSpacing="tighter"
        css={{ marginBlock: 'tighter' }}
      >
        {options.map((option) => (
          <FilterChip key={option.value} data-testid={`${name}-chip-${option.value}`} value={option.value}>
            {option.label}
          </FilterChip>
        ))}
      </ChipGroup>
    </FieldSet>
  );
};
